/**
 * gold-brackets.js
 * Replaces [bracketed text] inside rich-text / text blocks / hero content
 * with <span class="gold-bracket">bracketed text</span>. Brackets are
 * stripped. Runs on load, section reload, variant swap.
 */

const ROOT_SELECTOR = '.hero__content-wrapper, .text-block, rte-formatter, .rte';
const WRAP_CLASS = 'gold-bracket';
const BRACKET_RE = /\[([^\[\]\n]+?)\]/g;
const mutationObservers = new WeakMap();
let suppress = false;
let scheduled = 0;
const pending = new Set();

function transformNode(textNode) {
  const text = textNode.nodeValue;
  if (!text || text.indexOf('[') === -1) return false;
  BRACKET_RE.lastIndex = 0;
  if (!BRACKET_RE.test(text)) return false;
  BRACKET_RE.lastIndex = 0;

  const frag = document.createDocumentFragment();
  let last = 0;
  let m;
  while ((m = BRACKET_RE.exec(text)) !== null) {
    if (m.index > last) {
      frag.appendChild(document.createTextNode(text.slice(last, m.index)));
    }
    const span = document.createElement('span');
    span.className = WRAP_CLASS;
    span.textContent = m[1];
    frag.appendChild(span);
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    frag.appendChild(document.createTextNode(text.slice(last)));
  }
  textNode.parentNode.replaceChild(frag, textNode);
  return true;
}

function transform(root) {
  if (!root.isConnected) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || node.nodeValue.indexOf('[') === -1) {
        return NodeFilter.FILTER_REJECT;
      }
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (parent.closest('.' + WRAP_CLASS)) return NodeFilter.FILTER_REJECT;
      if (parent.closest('script, style')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  if (!nodes.length) return;

  suppress = true;
  try {
    nodes.forEach(transformNode);
  } finally {
    suppress = false;
  }
}

function flush() {
  scheduled = 0;
  const targets = Array.from(pending);
  pending.clear();
  targets.forEach((root) => transform(root));
}

function schedule(root) {
  pending.add(root);
  if (scheduled) return;
  scheduled = requestAnimationFrame(flush);
}

function scanAll() {
  document.querySelectorAll(ROOT_SELECTOR).forEach(schedule);
}

function attach(root) {
  if (mutationObservers.has(root)) return;
  const obs = new MutationObserver(() => {
    if (suppress) return;
    schedule(root);
  });
  obs.observe(root, { childList: true, subtree: true, characterData: true });
  mutationObservers.set(root, obs);
}

function refresh() {
  document.querySelectorAll(ROOT_SELECTOR).forEach(attach);
}

function init() {
  refresh();
  scanAll();
  document.addEventListener('shopify:section:load', () => {
    refresh();
    scanAll();
  });
  document.addEventListener('shopify:block:select', scanAll);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
